import React from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import { Bug, CheckCircle2, ImagePlus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/lib/AuthContext'
import { useCommunity } from '@/lib/CommunityContext'
import { useRoute } from '@/lib/RouteContext'
import { cn } from '@/lib/utils'

const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024
const DESCRIPTION_MAX = 4000
const CONTEXT_MAX = 2000
const SEND_TIMEOUT_MS = 10000
const TOAST_MS = 12000

type SendResult = { ok: true; eventId: string } | { ok: false; reason: 'notConfigured' | 'failed' }

/**
 * Waits for the transport's answer for this one event: a 2xx from ingest is
 * the only proof the report arrived. A timeout or an error status is a failure,
 * so the dialog keeps what the person wrote.
 */
const waitForDelivery = (client: NonNullable<ReturnType<typeof Sentry.getClient>>, eventId: string) =>
  new Promise<boolean>((resolve) => {
    let done = false
    const finish = (ok: boolean) => {
      if (done) return
      done = true
      window.clearTimeout(timer)
      unsubscribe()
      resolve(ok)
    }
    const unsubscribe = client.on('afterSendEvent', (event, response) => {
      if (event.event_id !== eventId) return
      const status = response?.statusCode
      /* A transport failure resolves with `{}` (no status): that is not a delivery. */
      finish(typeof status === 'number' && status >= 200 && status < 300)
    })
    const timer = window.setTimeout(() => finish(false), SEND_TIMEOUT_MS)
  })

const sendProblemReport = async (input: {
  description: string
  doing: string
  name: string
  email: string
  screenshot: File | null
  tags: Record<string, string>
}): Promise<SendResult> => {
  const client = Sentry.getClient()
  if (!client || !client.getDsn()) return { ok: false, reason: 'notConfigured' }

  const attachments = input.screenshot
    ? [{
      filename: input.screenshot.name || 'screenshot.png',
      data: new Uint8Array(await input.screenshot.arrayBuffer()),
      contentType: input.screenshot.type || 'image/png',
    }]
    : []

  const message = input.doing
    ? `${input.description}\n\n---\nWhat I was doing:\n${input.doing}`
    : input.description

  try {
    const eventId = Sentry.captureFeedback(
      {
        message,
        name: input.name || undefined,
        email: input.email || undefined,
        url: window.location.href,
        source: 'report-problem',
        tags: input.tags,
      },
      { attachments, includeReplay: true },
    )
    const delivered = await waitForDelivery(client, eventId)
    return delivered ? { ok: true, eventId } : { ok: false, reason: 'failed' }
  } catch {
    return { ok: false, reason: 'failed' }
  }
}

const appVersion = () => (typeof __APP_COMMIT__ === 'string' && __APP_COMMIT__ ? __APP_COMMIT__ : 'dev')

interface ReportProblemDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * "Reportar problema" (in the header avatar's panel since F-184): our own dialog
 * over Sentry's user feedback, so a report does not need the page to crash first.
 * The opener keeps this mounted while the panel closes, and the draft lives
 * here, outside the dialog content, so closing it or a failed send never throws
 * away what the person wrote.
 */
export function ReportProblemDialog({ open, onOpenChange: setOpen }: ReportProblemDialogProps) {
  const { t } = useTranslation('common')
  const { user } = useAuth()
  const { community } = useCommunity()
  const { currentPath } = useRoute()

  const [description, setDescription] = React.useState('')
  const [doing, setDoing] = React.useState('')
  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [screenshot, setScreenshot] = React.useState<File | null>(null)
  const [preview, setPreview] = React.useState<string | null>(null)
  const [descriptionError, setDescriptionError] = React.useState('')
  const [fileError, setFileError] = React.useState('')
  const [error, setError] = React.useState('')
  const [sending, setSending] = React.useState(false)
  const [sentId, setSentId] = React.useState<string | null>(null)
  const fileRef = React.useRef<HTMLInputElement>(null)

  /* Prefill from the session, without overwriting what the person already typed. */
  React.useEffect(() => {
    if (!open) return
    setName((value) => value || user?.name || '')
    setEmail((value) => value || user?.email || '')
  }, [open, user?.name, user?.email])

  React.useEffect(() => {
    if (!screenshot) { setPreview(null); return }
    const url = URL.createObjectURL(screenshot)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [screenshot])

  React.useEffect(() => {
    if (!sentId) return
    const timer = window.setTimeout(() => setSentId(null), TOAST_MS)
    return () => window.clearTimeout(timer)
  }, [sentId])

  const pickFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null
    event.target.value = ''
    setFileError('')
    if (!file) return
    if (!file.type.startsWith('image/')) { setFileError(t('reportProblem.errors.fileType')); return }
    if (file.size > MAX_SCREENSHOT_BYTES) { setFileError(t('reportProblem.errors.fileSize', { max: 5 })); return }
    setScreenshot(file)
  }

  const reset = () => {
    setDescription('')
    setDoing('')
    setScreenshot(null)
    setDescriptionError('')
    setFileError('')
    setError('')
  }

  const submit = async () => {
    setError('')
    const text = description.trim()
    if (!text) { setDescriptionError(t('reportProblem.errors.description')); return }
    setDescriptionError('')
    setSending(true)
    const result = await sendProblemReport({
      description: text,
      doing: doing.trim(),
      name: name.trim(),
      email: email.trim(),
      screenshot,
      tags: {
        route: currentPath.split(/[?#]/)[0] || '/',
        'community.id': community?.id ?? 'none',
        'app.version': appVersion(),
      },
    })
    setSending(false)
    if (!result.ok) {
      setError(t(result.reason === 'notConfigured' ? 'reportProblem.errors.notConfigured' : 'reportProblem.errors.failed'))
      return
    }
    reset()
    setOpen(false)
    setSentId(result.eventId)
  }

  const dirty = Boolean(description.trim() || doing.trim() || screenshot)

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => { if (!sending) setOpen(next) }}>
        <DialogContent className="max-h-[90vh] w-[calc(100%-1.5rem)] max-w-lg overflow-y-auto p-4 sm:p-6" data-testid="report-problem-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Bug className="h-5 w-5 text-primary" aria-hidden />{t('reportProblem.title')}</DialogTitle>
            <DialogDescription>{t('reportProblem.subtitle')}</DialogDescription>
          </DialogHeader>

          <div className="min-w-0 space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="report-problem-description" className="text-sm font-medium text-gray-700">
                {t('reportProblem.descriptionLabel')} <span className="text-red-600" aria-hidden>*</span>
              </label>
              <Textarea
                id="report-problem-description"
                data-testid="report-problem-description"
                value={description}
                maxLength={DESCRIPTION_MAX}
                rows={4}
                disabled={sending}
                required
                aria-invalid={Boolean(descriptionError)}
                placeholder={t('reportProblem.descriptionPlaceholder')}
                onChange={(e) => { setDescription(e.target.value); if (descriptionError) setDescriptionError('') }}
              />
              {descriptionError && <p role="alert" className="text-sm text-red-600">{descriptionError}</p>}
            </div>

            <div className="space-y-1.5">
              <label htmlFor="report-problem-doing" className="text-sm font-medium text-gray-700">{t('reportProblem.doingLabel')}</label>
              <Textarea
                id="report-problem-doing"
                data-testid="report-problem-doing"
                value={doing}
                maxLength={CONTEXT_MAX}
                rows={3}
                disabled={sending}
                placeholder={t('reportProblem.doingPlaceholder')}
                onChange={(e) => setDoing(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <span className="text-sm font-medium text-gray-700">{t('reportProblem.screenshotLabel')}</span>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickFile} data-testid="report-problem-file" />
              {screenshot && preview ? (
                <div className="flex min-w-0 items-center gap-3 rounded-lg border border-gray-200 p-2">
                  <img src={preview} alt="" className="h-14 w-20 flex-shrink-0 rounded object-cover" />
                  <span className="min-w-0 flex-1 truncate text-sm text-gray-700">{screenshot.name}</span>
                  <Button type="button" variant="ghost" size="sm" disabled={sending} onClick={() => setScreenshot(null)} aria-label={t('reportProblem.removeScreenshot')}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button type="button" variant="outline" className="w-full gap-2 sm:w-auto" disabled={sending} onClick={() => fileRef.current?.click()}>
                  <ImagePlus className="h-4 w-4" />{t('reportProblem.addScreenshot')}
                </Button>
              )}
              <p className="text-xs text-gray-500">{t('reportProblem.screenshotHint', { max: 5 })}</p>
              {fileError && <p role="alert" className="text-sm text-red-600">{fileError}</p>}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="min-w-0 space-y-1.5">
                <label htmlFor="report-problem-name" className="text-sm font-medium text-gray-700">{t('reportProblem.nameLabel')}</label>
                <Input id="report-problem-name" value={name} maxLength={120} disabled={sending} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="min-w-0 space-y-1.5">
                <label htmlFor="report-problem-email" className="text-sm font-medium text-gray-700">{t('reportProblem.emailLabel')}</label>
                <Input id="report-problem-email" type="email" value={email} maxLength={200} disabled={sending} onChange={(e) => setEmail(e.target.value)} />
              </div>
            </div>

            {dirty && !error && <p className="text-xs text-gray-500">{t('reportProblem.draftKept')}</p>}
            {error && <div role="alert" data-testid="report-problem-error" className="rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</div>}

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" className="w-full sm:w-auto" disabled={sending} onClick={() => setOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="button" variant="secondary" className="w-full sm:w-auto" disabled={sending} onClick={submit} data-testid="report-problem-submit">
                {sending ? t('reportProblem.sending') : t('reportProblem.submit')}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Portaled: the sidebar is transformed, which would pin a fixed toast to it instead of the screen. */}
      {sentId && createPortal(
        <div className="pointer-events-none fixed inset-x-4 bottom-4 z-[60] flex justify-center sm:inset-x-auto sm:right-4" aria-live="polite">
          <div role="status" data-testid="report-problem-toast" className="pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border border-gray-200 bg-white/95 p-4 text-gray-800 shadow-lg backdrop-blur-sm">
            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" aria-hidden />
            <div className="min-w-0 flex-1 text-sm">
              <p className="font-medium">{t('reportProblem.sent')}</p>
              <p className="mt-1 text-gray-600">
                {t('reportProblem.reference')} <span className="break-all font-mono text-xs" data-testid="report-problem-ref">{sentId}</span>
              </p>
            </div>
            <button type="button" aria-label={t('close')} className="flex-shrink-0 text-gray-400 hover:text-gray-600" onClick={() => setSentId(null)}>
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}

/**
 * "Reportar problema" as an icon in the header, beside the language globe and the bell (David,
 * 2026-10-29: it left the sidebar's personal group). The dialog lives with the button, so the
 * draft survives closing and reopening it.
 */
export function ReportProblemButton({ className }: { className?: string }) {
  const { t } = useTranslation('common')
  const [open, setOpen] = React.useState(false)
  const label = t('reportProblem.menu')
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={label}
        aria-label={label}
        data-testid="report-problem-open"
        className={cn('inline-flex h-10 w-10 items-center justify-center rounded-md text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary', className)}
      >
        <Bug className="h-[18px] w-[18px]" aria-hidden />
      </button>
      <ReportProblemDialog open={open} onOpenChange={setOpen} />
    </>
  )
}
